import { Link } from "react-router-dom";
import { Copy, Check, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface LinkCopyButtonProps {
  to: string | null;
  onCopy: (e: React.MouseEvent) => void;
  copied: boolean;
  label: ReactNode;
  copyAriaLabel: string;
  navAriaLabel: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Segmented control with two independent buttons that read as one unit:
 * [ copy icon | label + chevron → ]
 */
export function LinkCopyButton({
  to,
  onCopy,
  copied,
  label,
  copyAriaLabel,
  navAriaLabel,
  disabled = false,
  className,
}: LinkCopyButtonProps) {
  const isDisabled = disabled || !to;

  const navContent = (
    <>
      <span className="truncate text-[11px] font-medium">{label}</span>
      <ChevronRight className="h-3.5 w-3.5 ms-auto shrink-0 opacity-70" />
    </>
  );

  const navClasses = cn(
    "flex-1 min-w-0 h-11 inline-flex items-center gap-1 px-2.5",
    "text-foreground bg-background",
    !isDisabled && "hover:bg-muted/50 transition-colors",
    isDisabled && "opacity-50 cursor-not-allowed"
  );

  return (
    <div
      className={cn(
        "inline-flex items-stretch w-full rounded-md border border-input overflow-hidden bg-background",
        className
      )}
    >
      <button
        type="button"
        onClick={onCopy}
        disabled={isDisabled}
        aria-label={copyAriaLabel}
        title={copyAriaLabel}
        className={cn(
          "h-11 w-9 shrink-0 inline-flex items-center justify-center",
          "border-e border-input text-muted-foreground bg-background",
          !isDisabled && "hover:bg-muted/60 hover:text-foreground transition-colors",
          isDisabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>

      {isDisabled || !to ? (
        <div
          role="button"
          aria-disabled="true"
          aria-label={navAriaLabel}
          className={navClasses}
        >
          {navContent}
        </div>
      ) : (
        <Link to={to} aria-label={navAriaLabel} className={navClasses}>
          {navContent}
        </Link>
      )}
    </div>
  );
}
